/**
 * Content disputes: the learner's channel for "this item is wrong", and what we do
 * about it before a human has looked.
 *
 * Verbi ships the report button and nothing visible behind it — a learner who reports
 * a bad word keeps being drilled on it. That is the same failure as a confidently
 * wrong score, one layer down: we asserted something about the language and the
 * learner told us we were wrong, and we carried on asserting it.
 *
 * So a report has two immediate, automatic consequences, both decided here:
 *
 *   1. The reporter stops seeing the item at once. One person disagreeing is not
 *      proof the item is wrong, but it is proof that *this* learner will not learn
 *      anything from being shown it again while they believe it is broken.
 *   2. Once enough *distinct* learners agree, the item is withdrawn for everyone
 *      until content review resolves it. Distinct reporters, not reports: otherwise
 *      one annoyed learner tapping four times withdraws a correct item.
 *
 * Neither consequence deletes anything or asserts the item is actually wrong. They
 * suspend a claim we can no longer make with confidence, which is exactly what
 * `ScoreStatus.withheld` does for a score.
 */

/**
 * Why the learner says the item is wrong.
 *
 * `dialect_mismatch` has no equivalent in either competitor because neither models
 * dialect at all. It is the report we most expect to get and the most valuable one:
 * "this is Spanish, but not *my* Spanish" is a corpus bug we can actually fix.
 */
export const DISPUTE_REASONS = [
  'translation_wrong',
  'audio_wrong',
  'dialect_mismatch',
  'not_a_word',
  'offensive',
  'other',
] as const;

export type DisputeReason = (typeof DISPUTE_REASONS)[number];

/**
 * What the learner reads, in the order they are most likely to need.
 *
 * Here rather than in each client because it is the same six choices on three
 * platforms, and a reason worded differently on Android than on iOS would make the
 * `content_reported` analytics incomparable across them.
 */
export const DISPUTE_REASON_LABELS: Readonly<Record<DisputeReason, string>> = {
  translation_wrong: 'The translation is wrong',
  dialect_mismatch: "That's not how we say it here",
  audio_wrong: "The audio doesn't match",
  not_a_word: "This isn't a real word",
  offensive: 'This is offensive',
  other: 'Something else',
};

/** The reasons in the order they are offered, which is not the order of the enum. */
export const DISPUTE_REASON_ORDER: readonly DisputeReason[] = [
  'translation_wrong',
  'dialect_mismatch',
  'audio_wrong',
  'not_a_word',
  'offensive',
  'other',
];

export interface DisputeReport {
  readonly itemId: string;
  readonly reporterId: string;
  readonly reason: DisputeReason;
  readonly createdAt: number;
}

/**
 * Distinct reporters needed to withdraw an item from everyone.
 *
 * Three, not two: two is reachable by one learner and a friend, and withdrawing a
 * correct item costs every other learner the content. Three is low enough that a
 * genuinely broken item disappears within a day at any real volume.
 */
export const GLOBAL_WITHDRAWAL_THRESHOLD = 3;

/**
 * `offensive` withdraws on the first report, before anyone agrees.
 *
 * The asymmetry is deliberate and is the only place in this module where one report
 * is enough. Showing a learner something offensive three times while we wait for
 * corroboration is not a cost worth paying for the small chance the report is wrong;
 * withdrawing a clean item until review is.
 */
const WITHDRAWS_IMMEDIATELY: ReadonlySet<DisputeReason> = new Set<DisputeReason>(['offensive']);

export interface DisputeState {
  readonly itemId: string;
  /** Withdrawn from every learner until content review resolves it. */
  readonly withdrawn: boolean;
  /** Why, when withdrawn: the reason the most reporters gave, ties broken by severity. */
  readonly leadingReason: DisputeReason | null;
  readonly distinctReporters: number;
  /** Learners who must not be shown this item, whether or not it is globally withdrawn. */
  readonly suppressedFor: readonly string[];
}

/** Severity order for tie-breaking, most severe first. */
const SEVERITY: readonly DisputeReason[] = [
  'offensive',
  'not_a_word',
  'translation_wrong',
  'audio_wrong',
  'dialect_mismatch',
  'other',
];

/**
 * Folds every report for one item into the decision.
 *
 * Takes all reports rather than a count so the caller cannot accidentally pass report
 * count where distinct-reporter count was meant — the bug that would let one learner
 * withdraw an item alone.
 */
export function disputeState(itemId: string, reports: readonly DisputeReport[]): DisputeState {
  const mine = reports.filter((report) => report.itemId === itemId);
  const reporters = new Set(mine.map((report) => report.reporterId));

  const byReason = new Map<DisputeReason, Set<string>>();
  for (const report of mine) {
    const seen = byReason.get(report.reason) ?? new Set<string>();
    seen.add(report.reporterId);
    byReason.set(report.reason, seen);
  }

  const immediate = mine.some((report) => WITHDRAWS_IMMEDIATELY.has(report.reason));
  const withdrawn = immediate || reporters.size >= GLOBAL_WITHDRAWAL_THRESHOLD;

  let leadingReason: DisputeReason | null = null;
  if (withdrawn) {
    for (const reason of SEVERITY) {
      if (immediate && !WITHDRAWS_IMMEDIATELY.has(reason)) continue;
      const count = byReason.get(reason)?.size ?? 0;
      const leadingCount = leadingReason === null ? 0 : (byReason.get(leadingReason)?.size ?? 0);
      // SEVERITY is most-severe-first, so a strict `>` keeps the more severe reason on
      // a tie without a second comparison.
      if (count > leadingCount) leadingReason = reason;
    }
  }

  return {
    itemId,
    withdrawn,
    leadingReason,
    distinctReporters: reporters.size,
    suppressedFor: [...reporters].sort(),
  };
}

/**
 * The items a given learner must not be served right now.
 *
 * One pass over every report, so the caller can filter a whole review queue against
 * one call rather than one call per card.
 */
export function suppressedItemsFor(
  learnerId: string,
  reports: readonly DisputeReport[],
): ReadonlySet<string> {
  const suppressed = new Set<string>();
  for (const itemId of new Set(reports.map((report) => report.itemId))) {
    const state = disputeState(itemId, reports);
    if (state.withdrawn || state.suppressedFor.includes(learnerId)) suppressed.add(itemId);
  }
  return suppressed;
}

/**
 * What the learner is told when their report lands.
 *
 * It names the consequence that has already happened, because "thanks for your
 * feedback" with nothing behind it is what trains people to stop reporting.
 */
export function acknowledgement(state: DisputeState): string {
  if (state.withdrawn) {
    return "Thanks — we've pulled this one for everyone while we check it.";
  }
  return "Thanks — you won't see this one again while we check it.";
}
